// Reads/changes the active locale. The locale is owned by the `[lang]` URL
// segment (see the app's app/[lang]/_layout.tsx), not client-side state — switching
// language means navigating to the same page under a different `[lang]`
// prefix, so the choice is reflected in the URL (shareable, crawlable).

import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';

import { useLocaleLinks } from './locale-links-context';
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from './locales';

/** Swap the leading `/xx` locale segment of a pathname for `locale`. */
export function withLocale(pathname: string, locale: Locale): string {
  const rest = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '');
  return `/${locale}${rest}`;
}

/** Current locale (from the URL) + a setter that navigates to it. */
export function useLocale(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const { lang } = useLocalSearchParams<{ lang?: string }>();
  const pathname = usePathname();
  const router = useRouter();
  const localeLinks = useLocaleLinks();

  const locale = isSupportedLocale(lang) ? lang : DEFAULT_LOCALE;

  const setLocale = (next: Locale) => {
    if (localeLinks) {
      // On a page that provides per-locale destinations (e.g. an article,
      // whose slug differs per language), a prefix swap would 404 when it
      // guesses wrong. Use the exact link if this record has that
      // translation, otherwise the caller's safe fallback (e.g. the section's
      // listing page) rather than a guaranteed-broken guess.
      router.push(localeLinks[next] ?? (withLocale(pathname, next) as any));
      return;
    }
    // Destination is computed at runtime, outside the typed-routes literal set.
    router.push(withLocale(pathname, next) as any);
  };

  return { locale, setLocale };
}
